import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { createGalleryItem, getGalleryItem, updateGalleryItem } from '../../lib/api';
import { uploadImage } from '../../utils/imageUpload';

const GallerySchema = Yup.object().shape({
  title: Yup.string().required('Başlık gereklidir'),
  description: Yup.string().nullable(),
  image_url: Yup.string().required('Resim gereklidir'),
  type: Yup.string().oneOf(['product', 'project'], 'Geçerli bir tür seçin').required('Tür seçimi gereklidir'),
  is_active: Yup.boolean(),
});

const GalleryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: '',
    description: '',
    image_url: '',
    type: 'product',
    is_active: true,
  });

  const fetchGalleryItem = useCallback(async () => {
    try {
      const data = await getGalleryItem(id);
      if (data) {
        setInitialValues({
          ...data,
          description: data.description ?? '',
        });
      }
    } catch (error) {
      console.error('Error fetching gallery item:', error);
      toast.error('Galeri öğesi yüklenirken bir hata oluştu');
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchGalleryItem();
    }
  }, [id, fetchGalleryItem]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      if (id) {
        await updateGalleryItem(id, values);
      } else {
        await createGalleryItem(values);
      }

      toast.success(id ? 'Galeri öğesi güncellendi' : 'Galeri öğesi oluşturuldu');
      navigate('/admin/gallery');
    } catch (error) {
      console.error('Error saving gallery item:', error);
      toast.error(error.message || 'Galeri öğesi kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {id ? 'Galeri Öğesini Düzenle' : 'Yeni Galeri Öğesi Ekle'}
            </h2>
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={GallerySchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Başlık
                    </label>
                    <Field
                      type="text"
                      name="title"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    />
                    {errors.title && touched.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Açıklama
                    </label>
                    <Field
                      as="textarea"
                      name="description"
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    />
                    {errors.description && touched.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Tür
                    </label>
                    <Field
                      as="select"
                      name="type"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#dbc582] focus:ring-[#dbc582]"
                    >
                      <option value="product">Ürün</option>
                      <option value="project">Proje</option>
                    </Field>
                    {errors.type && touched.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Resim</label>
                    <div className="mt-1 flex items-center space-x-4">
                      {values.image_url && (
                        <img src={values.image_url} alt="Preview" className="h-32 w-32 object-cover rounded" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const publicUrl = await uploadImage(file, 'images', 'gallery');
                            setFieldValue('image_url', publicUrl);
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#dbc582] file:text-white hover:file:bg-[#c5b274]"
                      />
                    </div>
                    {errors.image_url && touched.image_url && (
                      <p className="mt-1 text-sm text-red-600">{errors.image_url}</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center">
                      <Field
                        type="checkbox"
                        name="is_active"
                        className="h-4 w-4 text-[#dbc582] focus:ring-[#dbc582] border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Aktif
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/gallery')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dbc582]"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#dbc582] hover:bg-[#c5b274] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dbc582] disabled:opacity-50"
                  >
                    {isSubmitting || loading ? 'Kaydediliyor...' : id ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default GalleryForm;
